type AuthorAvatarProps = {
  name: string;
  /** Headshot path, or empty when we don't have one yet. */
  image?: string | null;
  className?: string;
};

/**
 * Initials from a display name — "Ali Falahi" becomes "AF".
 *
 * Takes the first and last whitespace-separated parts so a middle name or a
 * long multi-part surname still yields two letters rather than three.
 */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return (first + last).toUpperCase();
}

/**
 * An author's headshot, falling back to a monogram.
 *
 * Not every author has a photo yet, and a broken image or an empty circle
 * reads as a bug. The monogram is a deliberate placeholder that looks
 * finished, so a missing headshot never blocks publishing the page — drop the
 * file in and set `image` in authors.json and it takes over with no other
 * change.
 *
 * The image is decorative here: every place this renders, the author's name
 * sits next to it in text, so announcing it again would be redundant.
 */
export function AuthorAvatar({ name, image, className }: AuthorAvatarProps) {
  const base = `overflow-hidden rounded-full ${className ?? ""}`;

  if (image) {
    return (
      <img
        src={image}
        alt=""
        loading="lazy"
        decoding="async"
        className={`${base} object-cover`}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={`${base} flex items-center justify-center bg-teal-light font-body font-bold text-teal-dark`}
    >
      {initials(name)}
    </span>
  );
}
